import React, { useState } from "react";
import { MenuItem, Typography, Checkbox } from "@mui/material";
import { Clear } from "@mui/icons-material";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  createAvtarName,
  differenceInDaysOrHours,
  hasImage,
} from "../../../../../utils";
import Avatar from "../../../../../components/Avatar";
import { deleteBoardUsers } from "../../../../../sevices/apiBoards";
import { currentUserId, isAdmin } from "../../../../../utils/constants";
import { useCustomSearchParams } from "../../../../../hooks/useCustomSearchParams";
import useToastPromise from "../../../../../hooks/useToast";
import { DeleteIcone, EditIcone } from "../../../../../components/UI/Icons";
import CustomMenuItem from "../../../../../components/Menu/CustomMenuItem";
import FormDialog from "../../../../../components/Dialog/FormDialog";
import { useResponsive } from "../../../../../hooks/useResponsive";
import useBoardIdByKey from "../../../../../hooks/useBoardIdByKey";

function RemoveUser({
  user,
  ownerId,
  onClick,
  closeUserList,
  isSelected,
  isColabrotar = false,
  setOpenEditUserDialog,
  boardOwnerId,
  handleRemoveColabrotor,
  boardName
}) {
  const { id } = useBoardIdByKey();
  const queryClient = useQueryClient();
  const [removeDialog, setRemoveDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const currentUser = useSelector((state) => state.users.logInUser);
  const isMobile = useResponsive("down", "md");
  const [selectedUserSearch, updateSearchParams, removeSearchParam] =
    useCustomSearchParams("search");
  const filterUser = selectedUserSearch?.split(",");
  const handleCloseDialog = () => {
    setRemoveDialog(false);
  };
  const { showToastPromise } = useToastPromise();

  const { mutate: removeMutate } = useMutation({
    mutationFn: async (data) => {
      return await showToastPromise(deleteBoardUsers, data);
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({
        queryKey: ["boardUsers", id],
      });
      setLoading(false);
      handleCloseDialog();
    },
    onError: (error) => {
      console.error("Error Delete Attachments", error);
      setLoading(false);
      handleCloseDialog();
    },
  });

  const callback = () => {
    setLoading(true);
    removeMutate({ board_id: id, user_id: user?.user_id });
  };

  const clearFilter = (e, user_id) => {
    e.stopPropagation();
    const newSelected =
      filterUser?.includes(String(user_id)) &&
      filterUser?.filter((el) => el !== String(user_id));
    if (filterUser?.[0] === String(user_id) && filterUser?.length === 1) {
      removeSearchParam("search");
    } else {
      updateSearchParams("search", newSelected?.join(","));
    }
  };

  const openPopUp = (e) => {
    e.stopPropagation();
    setRemoveDialog(true);
  };

  const handleEditPermission = (event) => {
    event.stopPropagation();
    setOpenEditUserDialog(user?.user_id);
  };

  function createUserName(fn, ln) {
    return `${fn} ${ln}`;
  }

  const handleDeleteColabrotor = (userId) => {
    handleRemoveColabrotor(userId)
    handleCloseDialog();
  }


  return (
    <>
      {removeDialog && (
        <FormDialog
          open={removeDialog}
          handleClose={handleCloseDialog}
          handleSubmit={isColabrotar ? () => handleDeleteColabrotor(user?.user_id) : callback}
          loading={loading}
          buttonTxt={"Delete"}
          title={
            isColabrotar
              ? "Confirmation: Delete collaborator"
              : "Confirmation: Delete User From Board"
          }
          alignActions={"center"}
          btnColor="#B71D18"
          cancelBtn={true}
          fullWidth={true}>
          <Typography className="align_text_center mb-2">
            {
              isColabrotar ? "Are you sure you want to delete this collaborator from board?" : "Are you sure you want to delete this user from board?"
            }
          </Typography>
        </FormDialog>
      )}
      <CustomMenuItem
        key={`user-avtar-dropdown-${user?.user_id}`}
        onClick={() => {
          onClick();
          closeUserList();
        }}
        className={`board-users-list-menu ${isSelected ? isMobile ? "active-assign" : "active-assign selected-border mb-2" : ""}`}
        onMouseEnter={() => setHoveredIndex(true)}
        onMouseLeave={() => setHoveredIndex(null)}
      >
        {isColabrotar ? (
          <>
            <Avatar
              key={`user-avtar-${user?.user_id}`}
              username={createUserName(
                user?.user?.first_name,
                user?.user?.last_name
              )}
              user_image={user?.user?.user_image}
              title={createUserName(
                user?.user?.first_name,
                user?.user?.last_name
              )}
              className="user-avtar-dropdown user-avatar mr-8"
              style={
                !hasImage(user?.user?.user_image) && {
                  bgcolor: user?.user?.profile_color?.background,
                  color: user?.user?.profile_color?.text,
                }
              }
              isAdmin={
                user?.is_admin ||
                user?.user_id == boardOwnerId ||
                (currentUserId() == user?.user_id && isAdmin())
              }>
              {createAvtarName(
                createUserName(user?.user?.first_name, user?.user?.last_name)
              )}
            </Avatar>
            <div>
              {createUserName(user?.user?.first_name, user?.user?.last_name)}
            </div>
          </>
        ) : (
          <>
            {isMobile && (
              <Checkbox
                checked={isSelected}
                // onChange={(e)=>  onClick()}
                className={`mobile-users-checkbox ${isSelected ? "mobile-users-list-item-checkbox" : ""}`} />

            )}
            <Avatar
              key={`user-avtar-${user?.user_id}`}
              username={user?.username}
              user_image={user?.user_image}
              title={user?.username}
              className="user-avtar-dropdown user-avatar mr-8"
              style={
                !hasImage(user?.user_image) && {
                  bgcolor: user?.profile_color?.background,
                  color: user?.profile_color?.text,
                }
              }
              isAdmin={
                user?.is_admin ||
                user?.user_id == boardOwnerId ||
                (currentUserId() == user?.user_id && isAdmin())
              }>
              {createAvtarName(user?.username)}
            </Avatar>
            <div className="mobile-users-list-item-content">
              <span>{user?.username}</span>
              <p className="user-last-seen-text ">
                {differenceInDaysOrHours(user?.last_seen)}
              </p>
            </div>
          </>
        )}

        <div className="remove-user-delete-icon">
          {(ownerId == currentUser?.user_id || isAdmin() || boardName?.data?.responseData?.board_permission?.is_admin) && hoveredIndex && (
            <>
              {" "}
              {!isColabrotar && user?.active && (
                <div className="mr-5" onClick={handleEditPermission}>
                  <EditIcone height={"20px"} width={"20px"} />
                </div>
              )}
              <div onClick={openPopUp}>
                <DeleteIcone height={"24px"} width={"24px"} />
              </div>
            </>
          )}
          {/* {!isMobile && !isColabrotar && isSelected && (
            <Clear onClick={(e) => clearFilter(e, user?.user_id)} />
          )} */}
        </div>
      </CustomMenuItem>
    </>
  );
}



export default RemoveUser;
